import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { reactRouterBrowserTracingIntegration, replayIntegration, type init } from '@sentry/react';
import { scrubRequestContent } from '@immo/observability';
import { env } from '@/lib/env';

type SentryInitOptions = Parameters<typeof init>[0];

const DEFAULT_TRACES_SAMPLE_RATE = 1.0;

function parseTracesSampleRate(raw: string | undefined): number {
  if (raw === undefined) return DEFAULT_TRACES_SAMPLE_RATE;
  const parsed = Number.parseFloat(raw);

  return Number.isFinite(parsed) ? parsed : DEFAULT_TRACES_SAMPLE_RATE;
}

/**
 * A view transition the browser skipped because the tab was hidden. React
 * Router starts one on every `viewTransition` navigation; when the tab is in
 * the background the browser drops the animation and rejects the transition's
 * promises, but the navigation itself completes (IMMOOS-WEB-APP-21/26).
 * Exported for unit testing only.
 */
export function isSkippedViewTransition(err: unknown): boolean {
  return (
    err instanceof Error &&
    err.name === 'InvalidStateError' &&
    /transition/i.test(err.message) &&
    /hidden/i.test(err.message)
  );
}

const beforeSend: NonNullable<SentryInitOptions['beforeSend']> = (event, hint) =>
  isSkippedViewTransition(hint.originalException) ? null : scrubRequestContent(event);

export function buildSentryInitOptions(dsn: string): SentryInitOptions {
  // Replays redact all text, inputs, and media by default. When the
  // VITE_SENTRY_REPLAY_NO_REDACT flag is set we capture them verbatim —
  // for testing only, never enable in production.
  const replayOptions = env.sentry.replayNoRedact
    ? { maskAllText: false, maskAllInputs: false, blockAllMedia: false }
    : {};

  return {
    dsn,
    environment: env.mode,
    release: env.sentry.release,
    tracesSampleRate: parseTracesSampleRate(env.sentry.tracesSampleRate),
    // Trace-propagation headers (`sentry-trace`, `baggage`) go to the API
    // origin ONLY — never to arbitrary third-party origins (CDNs, map tiles),
    // so trace ids are not leaked. An explicit allow-list, never a wildcard:
    // a permissive default here is a privacy defect, not a convenience.
    tracePropagationTargets: [env.apiOrigin],
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      replayIntegration(replayOptions),
      // The piece that was missing: without a browser tracing integration the
      // configured tracesSampleRate has been sampling an empty stream. Records
      // pageload/navigation transactions (named by matched route pattern, not
      // URL, so they aggregate) and http.client spans for API calls. React
      // Router v7 data-router instrumentation — paired with
      // wrapCreateBrowserRouter in routes.tsx.
      reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    sendDefaultPii: false,
    beforeSend,
    beforeSendTransaction: scrubRequestContent,
  };
}
